import React, { useState, useEffect, useCallback, useRef } from 'react';
import APIService from '../services/api';

/**
 * TATLease — Gestion de flotte, intégré en pleine page.
 *
 * L'accès passe par un lien signé obtenu à chaque ouverture : le jeton contenu
 * dans l'URL est à usage unique et vaut 5 minutes. On ne met donc JAMAIS cette
 * URL en cache, et on ne recharge jamais l'iframe sur la même URL — chaque
 * affichage demande un nouveau lien.
 *
 * L'iframe pointe sur cette URL signée (jamais sur la racine du site distant) :
 * c'est elle qui ouvre la session. Si le navigateur bloque les cookies des sites
 * intégrés, la session ne survit pas au premier saut et le site distant réaffiche
 * son écran de connexion — invisible pour nous (page d'un autre domaine). D'où le
 * repli « Ouvrir dans un onglet », mémorisable, où les cookies sont acceptés.
 *
 * Trois garde-fous pour tenir cette règle :
 *  - un compteur de demande : une réponse arrivée en retard ne peut plus
 *    remplacer un lien plus récent (sinon on afficherait un jeton déjà consommé) ;
 *  - un bouton « Recharger » qui redemande un lien au lieu de recharger l'iframe ;
 *  - un retour de veille / d'historique (pageshow) redemande aussi un lien, car
 *    celui affiché avant la mise en pause est mort.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

const btn = {
  display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', borderRadius: '9px',
  border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text-2)',
  fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
};
const btnMain = { ...btn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontWeight: 700 };

const PREF = 'tatlease_affichage';
const PREF_DUREE = 7 * 24 * 3600 * 1000;  // 7 jours

/**
 * Le repli en onglet répond à un blocage du navigateur, pas à un goût : il est
 * donc daté et se re-teste tout seul. Un blocage levé (côté navigateur ou côté
 * site de flotte) ramène l'affichage intégré sans que personne ait à y penser ;
 * s'il tient toujours, un clic remet le repli pour une nouvelle période.
 *
 * L'ancienne préférence (simple 'onglet', sans date) est traitée comme périmée :
 * elle a été posée pendant la panne de session, qui est corrigée.
 */
function lireMode() {
  try {
    const brut = localStorage.getItem(PREF);
    if (!brut) return 'page';
    const p = brut[0] === '{' ? JSON.parse(brut) : null;
    if (!p || p.mode !== 'onglet' || !(Date.now() - p.ts < PREF_DUREE)) {
      localStorage.removeItem(PREF);
      return 'page';
    }
    return 'onglet';
  } catch (e) { return 'page'; }
}

/**
 * Un lien d'accès porte un jeton. S'il n'en porte pas, le site distant affichera
 * son écran de connexion : autant le dire, plutôt que de laisser croire à un
 * blocage du navigateur. On n'empêche jamais l'affichage pour autant — le lien
 * est chargé tel quel, seul un avertissement s'ajoute.
 */
const sansJeton = (u) => {
  try { return !new URL(u).searchParams.has('token'); } catch (e) { return false; }
};

/** Origine seule d'une URL : ce qu'on peut tracer sans jamais sortir le jeton. */
const origineDe = (u) => {
  try { return new URL(u).origin; } catch (e) { return 'url illisible'; }
};

/**
 * Trace d'étape, au service du diagnostic serveur. Trois règles :
 * jamais bloquant (échec ignoré), jamais de jeton dans le detail, et `keepalive`
 * pour que la dernière trace parte même si l'onglet change dans la seconde.
 */
const trace = (etape, detail) => {
  try {
    fetch(`${BASE}/tatlease/trace`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${APIService.getToken()}` },
      body: JSON.stringify(detail == null ? { etape } : { etape, detail }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) { /* la télémétrie ne casse rien */ }
};

export default function TatLease({ showNotification }) {
  const [state, setState] = useState('loading');  // loading | ready | error
  const [url, setUrl] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [opening, setOpening] = useState(false);
  // 'page' : intégré ici. 'onglet' : l'utilisateur a constaté que son navigateur
  // n'y garde pas la session, on ouvre à côté et on s'en souvient.
  const [mode, setMode] = useState(() => lireMode());
  const setModeMemo = (m) => {
    try {
      if (m === 'onglet') localStorage.setItem(PREF, JSON.stringify({ mode: 'onglet', ts: Date.now() }));
      else localStorage.removeItem(PREF);
    } catch (e) {}
    setMode(m);
  };

  // Un appel = un lien. Renvoie l'URL, ou lève avec le message du serveur.
  // `raison` dit au serveur ce qui a déclenché la demande (ouverture d'écran,
  // rechargement, ouverture en onglet…) : ses journaux montrent alors quel geste
  // a produit quel appel. Paramètre purement informatif ; si le serveur le
  // refuse, on refait l'appel sans lui plutôt que de casser l'accès.
  const fetchEmbed = useCallback(async (raison) => {
    const appel = (avecRaison) => fetch(
      `${BASE}/tatlease/embed${avecRaison && raison ? `?raison=${encodeURIComponent(raison)}` : ''}`,
      { headers: { Authorization: `Bearer ${APIService.getToken()}` } },
    );
    let r;
    try {
      r = await appel(true);
      if ((r.status === 400 || r.status === 422) && raison) r = await appel(false);
    } catch (e) {
      // Réseau injoignable : pas de status à donner.
      trace('embed_erreur', `réseau : ${e.message}`);
      throw e;
    }
    const j = await r.json().catch(() => ({}));

    const echec = (message) => {
      trace('embed_erreur', `${r.status} — ${message}`);
      return new Error(message);
    };
    if (r.status === 403) {
      // Module retiré à l'organisation ou au compte : message standard.
      throw echec(j.scope === 'user'
        ? 'Cette fonctionnalité a été retirée pour votre compte.'
        : (j.message || j.error || 'Ce module est désactivé pour votre organisation.'));
    }
    // 400 (compte sans e-mail) et 503 (SSO non configuré) : le serveur explique
    // quoi corriger, on l'affiche tel quel.
    if (!r.ok || j.success === false || !j.url) throw echec(j.message || j.error || `HTTP ${r.status}`);
    trace('embed_recu');
    return j.url;
  }, []);

  // Numéro de la demande en cours. Toute réponse portant un numéro périmé est
  // jetée : deux clics rapides ne doivent pas remettre à l'écran le premier
  // lien (déjà consommé) parce qu'il est arrivé en dernier.
  const seq = useRef(0);

  const charger = useCallback((raison = 'ouverture') => {
    const n = ++seq.current;
    setState('loading'); setErreur(null); setUrl(null);
    fetchEmbed(raison)
      .then(u => { if (seq.current === n) { setUrl(u); setState('ready'); } })
      .catch(e => { if (seq.current === n) { setErreur(e.message); setState('error'); } });
  }, [fetchEmbed]);

  useEffect(() => {
    // En mode onglet, on ne demande rien : un lien ne sert qu'une fois, inutile
    // d'en brûler un que personne n'ouvrira.
    if (mode !== 'page') { seq.current++; setUrl(null); return undefined; }
    charger();
    // À la fermeture, la demande en cours est invalidée : le lien qui arriverait
    // après coup n'a plus rien à alimenter. On lit bien le compteur au moment du
    // nettoyage, pas celui capturé à l'entrée — d'où la règle désactivée.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { seq.current++; };
  }, [charger, mode]);

  // Montage de l'écran, quel que soit le mode d'affichage.
  useEffect(() => { trace('ouverture_module', mode === 'onglet' ? 'mode onglet mémorisé' : 'affichage dans la page'); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Le src est posé : on trace l'origine seule, jamais le jeton.
  useEffect(() => {
    if (mode === 'page' && url) trace('iframe_src_pose', origineDe(url));
  }, [mode, url]);

  // Retour depuis le cache de navigation (onglet réveillé, bouton « précédent »
  // du navigateur) : le lien affiché a expiré, on en redemande un.
  useEffect(() => {
    const onShow = (e) => { if (e.persisted && mode === 'page') charger('reveil'); };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, [charger, mode]);

  // Secours : un nouveau lien, ouvert dans un onglet.
  const openInTab = async () => {
    setOpening(true);
    trace('ouverture_onglet');
    // Onglet ouvert AVANT l'appel : sinon le navigateur bloque la popup.
    const tab = window.open('', '_blank', 'noopener');
    try {
      const u = await fetchEmbed('onglet');
      if (tab) tab.location = u; else window.open(u, '_blank', 'noopener');
    } catch (e) {
      if (tab) tab.close();
      showNotification?.(e.message, 'error');
    } finally { setOpening(false); }
  };


  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 96px)', minHeight: '520px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <div>
          <h1 className="ats-h1">Gestion de flotte<span className="ats-dot">.</span></h1>
          <p style={{ margin: '6px 0 0', fontSize: '13.5px', color: 'var(--text-2)' }}>Véhicules, contrats et échéances — TATLease, intégré à votre ERP.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {mode === 'page' && state === 'ready' && (
            <button onClick={() => charger('rechargement')} style={btn}
              title="L'accès expire après quelques minutes d'inactivité. Rechargez pour repartir sur une connexion neuve.">
              <BI icon="arrow-clockwise" /> Recharger
            </button>
          )}
          <button onClick={openInTab} disabled={opening} style={{ ...(mode === 'onglet' ? btnMain : btn), opacity: opening ? 0.6 : 1 }}>
            <BI icon={opening ? 'arrow-repeat' : 'box-arrow-up-right'} style={opening ? { animation: 'ao-spin .7s linear infinite' } : undefined} />
            {opening ? 'Ouverture…' : 'Ouvrir dans un onglet'}
          </button>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden', display: 'flex' }}>
        {mode === 'onglet' && (
          <div style={{ margin: 'auto', textAlign: 'center', maxWidth: '440px', padding: '24px' }}>
            <BI icon="truck" style={{ fontSize: '26px', color: 'var(--text-3, var(--text-2))' }} />
            <div style={{ margin: '12px 0 6px', fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>La gestion de flotte s'ouvre à côté</div>
            <div style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.5 }}>
              Votre navigateur ne garde pas la connexion quand la flotte est affichée dans cette page. Elle s'ouvre donc dans un nouvel onglet.
            </div>
            <div style={{ fontSize: '11.5px', color: 'var(--text-3, var(--text-2))', marginTop: '8px' }}>
              Ce choix se revérifie tout seul : le jour où l'affichage dans la page refonctionne, il reprend.
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '16px', flexWrap: 'wrap' }}>
              <button onClick={openInTab} disabled={opening} style={{ ...btnMain, opacity: opening ? 0.6 : 1 }}>
                <BI icon={opening ? 'arrow-repeat' : 'box-arrow-up-right'} style={opening ? { animation: 'ao-spin .7s linear infinite' } : undefined} />
                {opening ? 'Ouverture…' : 'Ouvrir la gestion de flotte'}
              </button>
              <button onClick={() => setModeMemo('page')} style={btn}><BI icon="window-stack" /> Réessayer dans la page</button>
            </div>
          </div>
        )}
        {mode === 'page' && state === 'loading' && (
          <div style={{ margin: 'auto', textAlign: 'center', color: 'var(--text-2)' }}>
            <BI icon="arrow-repeat" style={{ fontSize: '22px', animation: 'ao-spin .9s linear infinite' }} />
            <div style={{ marginTop: '10px', fontSize: '13px' }}>Connexion à la gestion de flotte…</div>
          </div>
        )}
        {mode === 'page' && state === 'error' && (
          <div style={{ margin: 'auto', textAlign: 'center', maxWidth: '440px', padding: '24px' }}>
            <BI icon="exclamation-triangle" style={{ fontSize: '24px', color: '#d97706' }} />
            <div style={{ margin: '10px 0 4px', fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>Ouverture impossible</div>
            <div style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.5 }}>{erreur}</div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '16px', flexWrap: 'wrap' }}>
              <button onClick={() => charger('reprise')} style={btnMain}><BI icon="arrow-clockwise" /> Réessayer</button>
              <button onClick={openInTab} disabled={opening} style={{ ...btn, opacity: opening ? 0.6 : 1 }}><BI icon="box-arrow-up-right" /> Ouvrir dans un onglet</button>
            </div>
          </div>
        )}
        {mode === 'page' && state === 'ready' && url && (
          // src = le lien signé reçu à l'instant, jamais l'adresse du site distant.
          // storage-access : on délègue au site intégré le droit de demander ses
          // cookies, seule façon pour lui de tenir la session dans une page tierce.
          <iframe key={url} src={url} title="Gestion de flotte"
            onLoad={() => trace('iframe_chargee')}
            onError={() => trace('iframe_erreur')}
            style={{ flex: 1, border: 'none', width: '100%', height: '100%' }}
            allow="clipboard-write; fullscreen; storage-access" />
        )}
      </div>

      {mode === 'page' && state === 'ready' && sansJeton(url) && (
        <p style={{ margin: '9px 2px 0', fontSize: '11.5px', color: '#b45309', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <BI icon="exclamation-triangle" />
          <span>Le lien d'accès reçu n'ouvre pas de session : l'écran de connexion de la flotte va s'afficher. Signalez-le, ce n'est pas votre navigateur.</span>
        </p>
      )}

      {mode === 'page' && state === 'ready' && (
        <p style={{ margin: '9px 2px 0', fontSize: '11.5px', color: 'var(--text-3, var(--text-2))', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span>Un écran de connexion, une page vide&nbsp;? Votre navigateur n'a pas gardé la session ici.</span>
          {/* Ce clic est la seule source fiable pour « login visible » : la page
              intégrée appartient à un autre domaine, le front ne peut pas la lire.
              La trace est donc marquée comme déclarée par l'utilisateur. */}
          <button onClick={() => { trace('login_tatlease_visible', 'déclaré par l’utilisateur (bascule en onglet)'); setModeMemo('onglet'); }}
            style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
            Toujours ouvrir dans un onglet
          </button>
        </p>
      )}
    </div>
  );
}
